import {Component, ChangeDetectionStrategy} from "@angular/core"
import {toSignal} from "@angular/core/rxjs-interop"
import {from, timer} from "rxjs"
import {map, scan, switchMap} from "rxjs/operators"
import {HintComponent, HintTypes} from "@common/components/hint/hint.component"
import {environment} from "@environment"

const POLL_INTERVAL_MS = 30_000
const HEALTH_TIMEOUT_MS = 5_000
// Only warn after the coordinator has been unreachable for this many consecutive polls. A deployment
// takes the instance down for a while, but a quick redeploy (or a one-off network hiccup) can clear
// within a poll cycle — this keeps the notice from flashing for those. At two polls the warning lags a
// genuine outage by ~one interval, which is an acceptable trade for not alarming staff during deploys.
const CONSECUTIVE_FAILURES_TO_WARN = 2

/**
 * Warns staff when the jobs coordinator (the `${env}-jobs` GCE instance) is not
 * running, so they know jobs won't be processed and can re-activate the system.
 *
 * The health check talks to the coordinator's `/health` endpoint directly, bypassing
 * the GraphQL backend — the first step towards the frontend consuming the jobs instance
 * directly (later via SSE, which suits a long-lived GCE instance better than Cloud Run).
 * The request uses `mode: "no-cors"` so the coordinator needs no CORS headers: a
 * reachable coordinator resolves the fetch (opaque response), a deactivated/unreachable
 * one rejects it (network error / timeout). Only reachability matters, not the body.
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-job-system-health",
    imports: [HintComponent],
    templateUrl: "./job-system-health.component.html",
})
export class JobSystemHealthComponent {
    protected readonly HintTypes = HintTypes

    protected readonly $isInactive = toSignal(
        timer(0, POLL_INTERVAL_MS).pipe(
            switchMap(() => from(this.probeUnreachable())),
            // Count consecutive unreachable probes; one reachable probe resets the run to zero. The
            // notice shows only once the count reaches the threshold, so a brief deploy blip never surfaces.
            scan((failures, unreachable) => (unreachable ? failures + 1 : 0), 0),
            map((failures) => failures >= CONSECUTIVE_FAILURES_TO_WARN),
        ),
        {initialValue: false},
    )

    private async probeUnreachable(): Promise<boolean> {
        try {
            await fetch(`${environment.jobsInstanceUrl}/health`, {mode: "no-cors", signal: AbortSignal.timeout(HEALTH_TIMEOUT_MS)})
            return false
        } catch {
            return true
        }
    }
}
